import { defineStore } from 'pinia'
import { computed } from 'vue'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'
import { useAsyncResource } from '../composables/useAsyncResource'

export interface ListItem {
  id: number
  list: string
  title: string
  url: string | null
  notes: string | null
  priority: 1 | 2 | 3 | null
  done: boolean
  done_at: string | null
  position: number | null
  created_at: string
}

export interface ListItemInput {
  list: string
  title: string
  url?: string
  notes?: string
  priority?: 1 | 2 | 3 | null
}

export const useListItemsStore = defineStore('listItems', () => {
  const {
    data: items,
    loading,
    error: loadError,
    refresh: fetchAll,
  } = useAsyncResource<ListItem[]>(
    () =>
      apiGet<{ list_items: ListItem[] }>('/api/v1/list_items', {
        failureLabel: 'Failed to load lists',
      }).then((res) => res.list_items),
    { initialValue: [], onError: () => {} },
  )

  const listNames = computed<string[]>(() => {
    const seen = new Set<string>()
    const names: string[] = []
    for (const item of items.value) {
      if (!seen.has(item.list)) {
        seen.add(item.list)
        names.push(item.list)
      }
    }
    return names.toSorted()
  })

  async function addItem(input: ListItemInput): Promise<void> {
    const res = await apiMutate<ListItem>('/api/v1/list_items', {
      method: 'POST',
      body: { list_item: input },
      failureLabel: 'Failed to add item',
    })
    items.value.push(res)
  }

  async function updateItem(id: number, attrs: Omit<Partial<ListItemInput>, 'list'>): Promise<void> {
    const res = await apiMutate<ListItem>(`/api/v1/list_items/${id}`, {
      method: 'PATCH',
      body: { list_item: attrs },
      failureLabel: 'Failed to update item',
    })
    const idx = items.value.findIndex((i) => i.id === id)
    if (idx !== -1) items.value[idx] = res
  }

  async function toggleDone(id: number, done: boolean): Promise<void> {
    const idx = items.value.findIndex((i) => i.id === id)
    if (idx !== -1) items.value[idx] = { ...items.value[idx], done }
    try {
      const res = await apiMutate<ListItem>(`/api/v1/list_items/${id}`, {
        method: 'PATCH',
        body: { list_item: { done } },
        failureLabel: 'Failed to update item',
      })
      if (idx !== -1) items.value[idx] = res
    } catch (e) {
      await fetchAll()
      throw e
    }
  }

  async function deleteItem(id: number): Promise<void> {
    await apiMutate<void>(`/api/v1/list_items/${id}`, {
      method: 'DELETE',
      failureLabel: 'Failed to delete item',
    })
    items.value = items.value.filter((i) => i.id !== id)
  }

  async function reorderItems(list: string, ids: number[]): Promise<void> {
    const others = items.value.filter((i) => i.list !== list)
    const listItems = items.value.filter((i) => i.list === list)
    const reordered = ids.map((id) => listItems.find((i) => i.id === id)).filter((i): i is ListItem => i !== undefined)
    items.value = [...others, ...reordered]

    await apiMutate<void>('/api/v1/list_items/reorder', {
      method: 'PATCH',
      body: { list, ids },
      failureLabel: 'Failed to reorder items',
    })
  }

  function byList(list: string): ListItem[] {
    return items.value.filter((i) => i.list === list)
  }

  return {
    items,
    listNames,
    loadError,
    loading,
    fetchAll,
    addItem,
    updateItem,
    toggleDone,
    deleteItem,
    reorderItems,
    byList,
  }
})
